import { CAExpenses, USExpenses } from "./Expenses";
import { resolveTenant } from "./resolveTenant";
import { Countries, Tenant } from "./Types";
import {
	consolidatedCreditTenantTheme,
	newLeafTenantTheme,
	type TenantTheme,
} from "../theme/tenantTheme";
import usLogo from "../assets/virtual-counselor.png";
import caLogo from "../assets/virtual-counselor-ca.png";
import usLogoSvg from "../assets/virtual-counselor.svg";
import caLogoSvg from "../assets/virtual-counselor-ca.svg";
import newLeafLogoPng from "../assets/new-leaf.png";
import consolidatedCreditFavicon from "../assets/consolidated-credit-favicon.ico";
import newLeafFavicon from "../assets/new-leaf-favicon.png";

type LanguageConfig = {
	[key: string]: { nativeName: string };
};

type SupportPhoneNumberConfig = {
	[key: string]: string;
};

type LegalUrlConfig = {
	[key: string]: {
		termsOfUseUrl: string;
		privacyPolicyUrl: string;
	};
};

type GtmConfig = {
	id: string;
	scriptBaseUrl: string;
};

type TenantRuntimeConfig = {
	tenant: Tenant;
	tenantDisplayName: string;
	legalName: string;
	supportPhoneNumbers: SupportPhoneNumberConfig;
	legalUrls: LegalUrlConfig;
	country: Countries;
	vicType: string;
	languages: LanguageConfig;
	logos: {
		png: string;
		svg: string;
	};
	favicon: string;
	gtm?: GtmConfig;
	incomeSources: {
		label: string;
		value: string;
	}[];
	expenses: typeof USExpenses;
	calendarTimeslots: {
		weekdays: string[];
		friday: string[];
		saturday: string[];
	};
	redirectUrl: string | undefined;
	theme: TenantTheme;
};

const tenant = resolveTenant();

const CONSOLIDATED_CREDIT_US_VIC_TYPE = "CCCS VIC";
const CONSOLIDATED_CREDIT_CA_VIC_TYPE = "CCCS CA VIC";
const NEW_LEAF_VIC_TYPE = "NewLeaf CA VIC";

export const USTimeslots = [
	"9:00 AM - 10:00 AM",
	"10:00 AM - 11:00 AM",
	"11:00 AM - 12:00 PM",
	"12:00 PM - 1:00 PM",
	"1:00 PM - 2:00 PM",
	"2:00 PM - 3:00 PM",
	"3:00 PM - 4:00 PM",
	"4:00 PM - 5:00 PM",
	"5:00 PM - 6:00 PM",
	"6:00 PM - 7:00 PM",
	"7:00 PM - 8:00 PM",
	"8:00 PM - 9:00 PM",
];

export const CATimeslots = [
	"9:00 AM - 10:00 AM",
	"10:00 AM - 11:00 AM",
	"11:00 AM - 12:00 PM",
	"12:00 PM - 1:00 PM",
	"1:00 PM - 2:00 PM",
	"2:00 PM - 3:00 PM",
	"3:00 PM - 4:00 PM",
	"4:00 PM - 5:00 PM",
	"5:00 PM - 6:00 PM",
	"6:00 PM - 7:00 PM",
	"7:00 PM - 8:00 PM",
];

/** New Leaf callback hours (interpreted as America/New_York in CallbackForm). Mon–Thu 9am–7pm; Fri 9am–6pm. */
const newLeafWeekdayTimeslots = [
	"9:00 AM - 10:00 AM",
	"10:00 AM - 11:00 AM",
	"11:00 AM - 12:00 PM",
	"12:00 PM - 1:00 PM",
	"1:00 PM - 2:00 PM",
	"2:00 PM - 3:00 PM",
	"3:00 PM - 4:00 PM",
	"4:00 PM - 5:00 PM",
	"5:00 PM - 6:00 PM",
	"6:00 PM - 7:00 PM",
];

const newLeafFridayTimeslots = [
	"9:00 AM - 10:00 AM",
	"10:00 AM - 11:00 AM",
	"11:00 AM - 12:00 PM",
	"12:00 PM - 1:00 PM",
	"1:00 PM - 2:00 PM",
	"2:00 PM - 3:00 PM",
	"3:00 PM - 4:00 PM",
	"4:00 PM - 5:00 PM",
	"5:00 PM - 6:00 PM",
];

const USIncomeSources = [
	{
		label: "incomeForm.sourceOptions.primaryIncome",
		value: "Applicant Primary Income",
	},
	{
		label: "incomeForm.sourceOptions.secondaryIncome",
		value: "Applicant Secondary Income",
	},
	{
		label: "incomeForm.sourceOptions.childSupportAlimony",
		value: "Applicant Alimony/Child Support",
	},
	{
		label: "incomeForm.sourceOptions.socialSecurity",
		value: "Applicant Social Security",
	},
	{
		label: "incomeForm.sourceOptions.pension",
		value: "Applicant Pension",
	},
	{
		label: "incomeForm.sourceOptions.disability",
		value: "Applicant Disability",
	},
	{
		label: "incomeForm.sourceOptions.unemployment",
		value: "Applicant Unemployment",
	},
	{
		label: "incomeForm.sourceOptions.rentalIncome",
		value: "Applicant Rental Income",
	},
	{
		label: "incomeForm.sourceOptions.other",
		value: "Applicant Other Income",
	},
];

const CAIncomeSources = [
	{
		label: "incomeForm.sourceOptions.primaryIncome",
		value: "Applicant Primary Income",
	},
	{
		label: "incomeForm.sourceOptions.secondaryIncome",
		value: "Applicant Secondary Income",
	},
	{
		label: "incomeForm.sourceOptions.childTaxBenefit",
		value: "Child Tax Benefit",
	},
	{
		label: "incomeForm.sourceOptions.govtPension",
		value: "Applicant Social Security",
	},
	{
		label: "incomeForm.sourceOptions.privatePension",
		value: "Applicant Pension",
	},
	{
		label: "incomeForm.sourceOptions.publicAssistance",
		value: "Public Assistance",
	},
	{
		label: "incomeForm.sourceOptions.childSupportAlimony",
		value: "Applicant Alimony/Child Support",
	},
	{
		label: "incomeForm.sourceOptions.rentalIncome",
		value: "Applicant Rental Income",
	},
	{
		label: "incomeForm.sourceOptions.other",
		value: "Applicant Other Income",
	},
];

const usLanguages: LanguageConfig = {
	en: { nativeName: "English" },
	es: { nativeName: "Español" },
};

const consolidatedCreditCALanguages: LanguageConfig = {
	"en-CA": { nativeName: "English" },
	"es-CA": { nativeName: "Español" },
	"fr-CA": { nativeName: "Français" },
};

const newLeafLanguages: LanguageConfig = {
	"en-CA": { nativeName: "English" },
	"es-CA": { nativeName: "Español" },
};

const consolidatedCreditConfig = {
	US: {
		tenant: Tenant.CONSOLIDATED_CREDIT,
		tenantDisplayName: "Consolidated Credit",
		legalName: "Consolidated Credit Solutions, Inc",
		supportPhoneNumbers: {
			en: "1-888-298-1691",
			es: "1-888-298-2161",
		},
		legalUrls: {
			en: {
				termsOfUseUrl: "https://www.consolidatedcredit.org/terms-conditions/",
				privacyPolicyUrl: "https://www.consolidatedcredit.org/privacy/",
			},
			es: {
				termsOfUseUrl:
					"https://www.consolidatedcredit.org/es/terminos-y-condiciones-online/",
				privacyPolicyUrl:
					"https://www.consolidatedcredit.org/es/politica-de-privacidad/",
			},
		},
		country: "US" as const,
		vicType: CONSOLIDATED_CREDIT_US_VIC_TYPE,
		languages: usLanguages,
		logos: {
			png: usLogo,
			svg: usLogoSvg,
		},
		favicon: consolidatedCreditFavicon,
		gtm: {
			id: "GTM-TW7XHF7",
			scriptBaseUrl: "https://gtm.consolidatedcredit.org/gtm.js?id=",
		},
		incomeSources: USIncomeSources,
		expenses: USExpenses,
		calendarTimeslots: {
			weekdays: USTimeslots,
			friday: USTimeslots,
			saturday: USTimeslots.slice(1, 5),
		},
		redirectUrl: process.env.REACT_APP_REDIRECT_URL,
		theme: consolidatedCreditTenantTheme,
	},
	CA: {
		tenant: Tenant.CONSOLIDATED_CREDIT_CANADA,
		tenantDisplayName: "Consolidated Credit",
		legalName: "Consolidated Credit Solutions, Inc",
		supportPhoneNumbers: {
			"en-CA": "1-844-867-2731",
			"es-CA": "1-844-867-2731",
			"fr-CA": "1-844-867-2731",
		},
		legalUrls: {
			"en-CA": {
				termsOfUseUrl: "https://www.consolidatedcreditcanada.ca/terms-of-use/",
				privacyPolicyUrl:
					"https://www.consolidatedcreditcanada.ca/privacy-statement/",
			},
			"es-CA": {
				termsOfUseUrl:
					"https://es.consolidatedcreditcanada.ca/terminos-de-uso/",
				privacyPolicyUrl:
					"https://es.consolidatedcreditcanada.ca/politicas-de-privacidad/",
			},
			"fr-CA": {
				termsOfUseUrl:
					"https://fr.consolidatedcreditcanada.ca/conditions-generales-dutilisation/",
				privacyPolicyUrl:
					"https://fr.consolidatedcreditcanada.ca/politique-de-confidentialite/",
			},
		},
		country: "CA" as const,
		vicType: CONSOLIDATED_CREDIT_CA_VIC_TYPE,
		languages: consolidatedCreditCALanguages,
		logos: {
			png: caLogo,
			svg: caLogoSvg,
		},
		favicon: consolidatedCreditFavicon,
		gtm: {
			id: "GTM-TW7XHF7",
			scriptBaseUrl: "https://gtm.consolidatedcreditcanada.ca/gtm.js?id=",
		},
		incomeSources: CAIncomeSources,
		expenses: CAExpenses,
		calendarTimeslots: {
			weekdays: CATimeslots,
			friday: CATimeslots.slice(0, 9),
			saturday: CATimeslots.slice(1, 5),
		},
		redirectUrl: process.env.REACT_APP_REDIRECT_URL_CA,
		theme: consolidatedCreditTenantTheme,
	},
};

const tenantConfig = {
	[Tenant.CONSOLIDATED_CREDIT]: consolidatedCreditConfig.US,
	[Tenant.CONSOLIDATED_CREDIT_CANADA]: consolidatedCreditConfig.CA,
	[Tenant.NEW_LEAF]: {
		tenant: Tenant.NEW_LEAF,
		tenantDisplayName: "New Leaf",
		legalName: "New Leaf",
		supportPhoneNumbers: {
			"en-CA": "1-844-686-8371",
			"es-CA": "1-844-686-8371",
		},
		legalUrls: {
			"en-CA": {
				termsOfUseUrl: "https://newleafdebt.ca/terms-of-use/",
				privacyPolicyUrl: "https://newleafdebt.ca/privacy/",
			},
			"es-CA": {
				termsOfUseUrl: "https://newleafdebt.ca/terms-of-use/",
				privacyPolicyUrl: "https://newleafdebt.ca/privacy/",
			},
		},
		country: "CA" as const,
		vicType: NEW_LEAF_VIC_TYPE,
		languages: newLeafLanguages,
		logos: {
			png: newLeafLogoPng,
			// No vector asset yet; PNG works as <img src> (e.g. header Logo).
			svg: newLeafLogoPng,
		},
		favicon: newLeafFavicon,
		gtm: {
			id: "GTM-TW7XHF7",
			scriptBaseUrl: "https://www.googletagmanager.com/gtm.js?id=",
		},
		incomeSources: CAIncomeSources,
		expenses: CAExpenses,
		calendarTimeslots: {
			weekdays: newLeafWeekdayTimeslots,
			friday: newLeafFridayTimeslots,
			saturday: [],
		},
		redirectUrl: process.env.REACT_APP_REDIRECT_URL_NEW_LEAF,
		theme: newLeafTenantTheme,
	},
};

const config: TenantRuntimeConfig = tenantConfig[tenant];

/** Lookup runtime config by tenant key (used with `resolveTenantFromHostname` in tests). */
export function getTenantRuntimeConfig(tenant: Tenant): TenantRuntimeConfig {
	return tenantConfig[tenant];
}

export function getSupportPhoneNumber(
	config: TenantRuntimeConfig,
	language: string
): string {
	return (
		config.supportPhoneNumbers[language] ??
		Object.values(config.supportPhoneNumbers)[0]
	);
}

export function getLegalUrls(
	config: TenantRuntimeConfig,
	language: string
): { termsOfUseUrl: string; privacyPolicyUrl: string } {
	return config.legalUrls[language] ?? Object.values(config.legalUrls)[0];
}

export { config };
export type { TenantTheme } from "../theme/tenantTheme";
